import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { isAxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { CHAR_MAX_LENGTH } from 'shared/constants/limits'
import {
  CustomFieldApi,
  CustomFieldType,
  type ICustomFieldType,
  type IResponseCustomFieldError,
} from 'entities/CustomField/api'
import { CustomField } from 'entities/CustomField/model'
import { CustomFieldModalContent } from 'entities/CustomField/ui/CustomFieldModal/ui/CustomFieldModalContent'

type ICustomFieldModalParams = {
  editId?: number
  initial?: { name: string; fieldType: ICustomFieldType; visible: boolean }
  onSuccess?: () => Promise<void>
  onCreated?: (field: CustomField) => void
  onClose?: () => void
}

export class CustomFieldModalStore {
  private _name = ''
  private _fieldType: ICustomFieldType | null = null
  private _visible = false
  private _nameError: string | null = null
  private _typeError: string | null = null
  private _loading = false
  private readonly _modalId: string

  constructor(private _params: ICustomFieldModalParams) {
    const { editId, initial } = _params
    const isEdit = editId !== undefined

    if (isEdit && initial) {
      this._name = initial.name
      this._fieldType = initial.fieldType
      this._visible = initial.visible
    }

    this._modalId = nanoid()

    makeAutoObservable(this)

    this._openModal()
  }

  setName = (name: string) => {
    this._name = name
    this._nameError = null
  }

  private _setFieldType = (type: ICustomFieldType) => {
    this._fieldType = type
    this._typeError = null
  }

  setFieldTypeFromDropdown = (value: string) => {
    const type = Object.values(CustomFieldType).find((t) => t === value)
    if (type) this._setFieldType(type)
  }

  toggleVisible = () => {
    this._visible = !this._visible
  }

  private _setNameError = (error: string) => {
    this._nameError = error
  }

  private _setTypeError = (error: string) => {
    this._typeError = error
  }

  validate = (): boolean => {
    let valid = true

    if (!this.trimmedName.length) {
      this._nameError = 'Please enter a name'
      valid = false
    }

    if (!this._fieldType) {
      this._typeError = 'Please select a type'
      valid = false
    }

    if (this.isNameOverLimit) {
      valid = false
    }

    return valid
  }

  get name() {
    return this._name
  }

  get fieldType() {
    return this._fieldType
  }

  get visible() {
    return this._visible
  }

  get nameError() {
    return this._nameError
  }

  get typeError() {
    return this._typeError
  }

  get trimmedName() {
    return this._name.trim()
  }

  get isNameOverLimit() {
    return this.trimmedName.length > CHAR_MAX_LENGTH
  }

  private _openModal = () => {
    const isEdit = this._params.editId !== undefined

    modalStore.addModal({
      id: this._modalId,
      title: isEdit ? 'Edit field' : 'New field',
      ModalContent: () => <CustomFieldModalContent store={this} />,
      width: 480,
      onClose: this._closeModal,
      primaryAction: {
        text: isEdit ? 'Save' : 'Create',
        onAction: this._handleSave,
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: this._closeModal,
      },
    })
  }

  private _handleSave = async () => {
    if (!this.validate()) return
    if (this._loading) return

    const { fieldType } = this
    if (!fieldType) return

    const { editId, onSuccess, onCreated } = this._params
    const isEdit = editId !== undefined

    try {
      runInAction(() => {
        this._loading = true
      })

      if (isEdit) {
        await CustomFieldApi.updateCustomField(editId, {
          name: this.trimmedName,
          type: fieldType,
          visible: this.visible,
        })
      } else {
        const {
          data: { data: fieldData },
        } = await CustomFieldApi.createCustomField({
          name: this.trimmedName,
          type: fieldType,
          visible: this.visible,
        })

        onCreated?.(new CustomField(fieldData))
      }

      modalStore.removeModal(this._modalId)

      await onSuccess?.()

      showToast({
        type: 'success',
        title: isEdit ? 'Custom field updated' : 'Custom field created',
      })
    } catch (error) {
      logger.error(error)

      if (isAxiosError<IResponseCustomFieldError>(error)) {
        const { data } = error.response ?? {}
        const nameError = data?.name?.[0]
        const typeError = data?.type?.[0]
        let hasFieldError = false

        if (nameError) {
          this._setNameError(nameError)
          hasFieldError = true
        }

        if (typeError) {
          this._setTypeError(typeError)
          hasFieldError = true
        }

        if (hasFieldError) return
      }

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  private _closeModal = () => {
    modalStore.removeModal(this._modalId)
    this._params.onClose?.()
  }
}
